import { Tabs as TabsPrimitive } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '../cn';

export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

export const TabsList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof TabsPrimitive.List>>(function TabsList(
  { className, ...props },
  ref,
) {
  return <TabsPrimitive.List ref={ref} className={cn('flex items-end gap-1 border-b border-border', className)} {...props} />;
});

/**
 * The ONE tab style (record tabs, in-page tabs, MIS sub-navigation, queue strip): active = accent label, semibold,
 * 2px accent underline; inactive = secondary ink with a border-strong underline on hover. Counts are small pills.
 */
export const tabTriggerClass =
  '-mb-px inline-flex h-9 items-center gap-1.5 border-b-2 border-transparent px-2.5 text-body whitespace-nowrap text-ink-secondary hover:border-border-strong hover:text-ink data-[state=active]:border-accent data-[state=active]:font-semibold data-[state=active]:text-accent aria-[current=page]:border-accent aria-[current=page]:font-semibold aria-[current=page]:text-accent';

export const TabsTrigger = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>>(function TabsTrigger(
  { className, ...props },
  ref,
) {
  return <TabsPrimitive.Trigger ref={ref} className={cn(tabTriggerClass, className)} {...props} />;
});

/** Count pill next to a tab label. `attention` gives it an amber outline and text when something there blocks progress. */
export function TabCount({ children, attention }: { children: React.ReactNode; attention?: boolean }) {
  return (
    <span
      data-attention={attention || undefined}
      className={cn(
        'tabular inline-flex h-4.5 min-w-5 items-center justify-center rounded-sm px-1 text-meta font-medium',
        attention ? 'bg-surface text-warning ring-1 ring-warning-edge ring-inset' : 'bg-surface-muted text-ink-secondary',
      )}
    >
      {children}
    </span>
  );
}
